import { useState, useEffect } from 'react';
import { api, formatCurrency } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import NumericKeypad from '../components/NumericKeypad';

const EMPTY = { name:'', target_amount:'', current_amount:'', icon:'🎯', color:'#00D4AA', deadline:'' };
const ICONS = ['🎯','✈️','🏠','🚗','💻','📱','🎓','💍','🏋️','🌴','💰','🎸'];
const COLORS = ['#00D4AA','#7C6FFF','#FF5470','#FFB800','#00B8FF','#FF9F43','#A29BFE','#FD79A8'];

export default function Savings() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showKeypad, setShowKeypad] = useState(false);
  const [activeKeypad, setActiveKeypad] = useState(null); // 'target' o 'current'
  const [depositModal, setDepositModal] = useState(null);
  const [depositAmt, setDepositAmt] = useState('');
  const currency = user?.currency || 'COP';

  const load = async () => {
    setLoading(true);
    try { setGoals(await api.get('/savings')); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setShowModal(true); };
  const openEdit = (g) => {
    setForm({ name:g.name, target_amount:g.target_amount, current_amount:g.current_amount,
      icon:g.icon, color:g.color, deadline:g.deadline||'' });
    setEditId(g.id); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editId) { await api.put(`/savings/${editId}`, form); toast.success('Meta actualizada'); }
      else { await api.post('/savings', form); toast.success('¡Meta creada!'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta meta?')) return;
    try { await api.delete(`/savings/${id}`); toast.success('Meta eliminada'); load(); }
    catch (err) { toast.error(err.message); }
  };

  const handleDeposit = async () => {
    if (!depositAmt || isNaN(depositAmt)) return;
    const g = goals.find(x => x.id === depositModal);
    const newAmt = Math.min(g.current_amount + parseFloat(depositAmt), g.target_amount);
    try {
      await api.put(`/savings/${depositModal}`, { ...g, current_amount: newAmt });
      toast.success(newAmt >= g.target_amount ? '🎉 ¡Meta alcanzada!' : 'Depósito registrado');
      setDepositModal(null); setDepositAmt(''); load();
    } catch (err) { toast.error(err.message); }
  };

  const totalSaved = goals.reduce((s,g) => s + g.current_amount, 0);
  const totalTarget = goals.reduce((s,g) => s + g.target_amount, 0);

  return (
    <div style={styles.page} className="animate-fadeIn">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem' }}>Metas de ahorro</h2>
          {goals.length > 0 && (
            <p style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginTop:4 }}>
              {formatCurrency(totalSaved, currency)} de {formatCurrency(totalTarget, currency)}
            </p>
          )}
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Nueva meta</button>
      </div>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}>
          <div className="spinner" style={{ width:32, height:32 }} />
        </div>
      ) : goals.length === 0 ? (
        <div style={styles.empty}>
          <span style={{ fontSize:52 }}>💫</span>
          <p style={{ fontWeight:600, fontSize:'1.05rem' }}>Sin metas aún</p>
          <p style={{ fontSize:'0.85rem' }}>Define hacia dónde va tu dinero</p>
          <button className="btn btn-primary" onClick={openAdd}>Crear primera meta</button>
        </div>
      ) : (
        <div style={styles.grid}>
          {goals.map(g => {
            const pct = g.target_amount > 0 ? Math.min((g.current_amount/g.target_amount)*100, 100) : 0;
            const done = pct >= 100;
            const remaining = g.target_amount - g.current_amount;
            const daysLeft = g.deadline ? Math.ceil((new Date(g.deadline) - new Date()) / 86400000) : null;
            return (
              <div key={g.id} className="card" style={{ position:'relative', overflow:'hidden' }}>
                {done && (
                  <div style={styles.doneBanner}>🎉 ¡Completada!</div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{
                      width:50, height:50, borderRadius:16,
                      background: `${g.color}20`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:24,
                    }}>
                      {g.icon}
                    </div>
                    <div>
                      <p style={{ fontWeight:700, fontSize:'1rem' }}>{g.name}</p>
                      {daysLeft !== null && (
                        <span className={`badge ${daysLeft < 30 ? 'badge-red' : 'badge-blue'}`} style={{ marginTop:4, fontSize:'0.7rem' }}>
                          {daysLeft > 0 ? `${daysLeft} días` : 'Vencida'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(g)}>✏️</button>
                    <button className="btn btn-danger btn-icon btn-sm" onClick={() => handleDelete(g.id)}>🗑</button>
                  </div>
                </div>

                <div style={{ marginBottom:14 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontWeight:800, fontSize:'1.25rem', fontFamily:'var(--font-display)', color: g.color }}>
                      {formatCurrency(g.current_amount, currency)}
                    </span>
                    <span style={{ color:'var(--text-secondary)', fontSize:'0.85rem', alignSelf:'flex-end' }}>
                      / {formatCurrency(g.target_amount, currency)}
                    </span>
                  </div>
                  <div className="progress-bar" style={{ height:10 }}>
                    <div className="progress-fill" style={{ width:`${pct}%`, background: g.color }} />
                  </div>
                  <p style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:6 }}>
                    {Math.round(pct)}% · {done ? '¡Completada!' : `Faltan ${formatCurrency(remaining, currency)}`}
                  </p>
                </div>

                {!done && (
                  <button className="btn btn-secondary btn-full btn-sm" onClick={() => { setDepositModal(g.id); setDepositAmt(''); }}>
                    + Agregar fondos
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">{editId ? 'Editar' : 'Nueva'} meta</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="form-group">
                <label className="form-label">Nombre de la meta</label>
                <input type="text" className="form-input" placeholder="Ej: Vacaciones en Cartagena"
                  value={form.name} onChange={e => setForm({...form, name:e.target.value})} required />
              </div>
             <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div className="form-group">
                  <label className="form-label">Meta (total)</label>
                  <div onClick={() => { setActiveKeypad('target'); setShowKeypad(true); }} style={styles.amountDisplay}>
                    <span style={{ fontSize: form.target_amount ? '1.2rem' : '0.85rem', fontWeight:800, fontFamily:'var(--font-display)', color: form.target_amount ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {form.target_amount ? formatCurrency(parseFloat(form.target_amount), currency) : 'Toca aquí'}
                    </span>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Ya tengo</label>
                  <div onClick={() => { setActiveKeypad('current'); setShowKeypad(true); }} style={styles.amountDisplay}>
                    <span style={{ fontSize: form.current_amount ? '1.2rem' : '0.85rem', fontWeight:800, fontFamily:'var(--font-display)', color: form.current_amount ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {form.current_amount ? formatCurrency(parseFloat(form.current_amount), currency) : 'Toca aquí'}
                    </span>
                  </div>
                </div>
              </div>
              {showKeypad && (
                <NumericKeypad
                  value={activeKeypad === 'target' ? form.target_amount : form.current_amount}
                  onChange={(val) => setForm({...form, [activeKeypad === 'target' ? 'target_amount' : 'current_amount']: val})}
                  onConfirm={() => setShowKeypad(false)}
                  currency={currency}
                  label={activeKeypad === 'target' ? 'Meta total' : 'Ya tengo'}
                />
              )}
              <div className="form-group">
                <label className="form-label">Fecha límite (opcional)</label>
                <input type="date" className="form-input" value={form.deadline}
                  onChange={e => setForm({...form, deadline:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Ícono</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => setForm({...form, icon:ic})}
                      style={{ width:40, height:40, borderRadius:10, border: form.icon===ic ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: form.icon===ic ? 'var(--accent-glow)' : 'transparent', cursor:'pointer', fontSize:20 }}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setForm({...form, color:c})}
                      style={{ width:32, height:32, borderRadius:'50%', background:c, border: form.color===c ? '3px solid white' : 'none', cursor:'pointer' }} />
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" className="btn btn-secondary" style={{ flex:1 }} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" style={{ flex:2 }} disabled={saving}>
                  {saving ? <span className="spinner" /> : (editId ? 'Guardar' : 'Crear meta')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deposit modal */}
      {depositModal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && setDepositModal(null)}>
          <div className="modal" style={{ maxWidth:360 }}>
            <div className="modal-header">
              <h3 className="modal-title">Agregar fondos</h3>
              <button className="modal-close" onClick={() => setDepositModal(null)}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div className="form-group">
                <label className="form-label">¿Cuánto vas a agregar?</label>
                <div onClick={() => setShowKeypad(true)} style={styles.amountDisplay}>
                  <span style={{ fontSize: depositAmt ? '1.6rem' : '1rem', fontWeight:800, fontFamily:'var(--font-display)', color: depositAmt ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {depositAmt ? formatCurrency(parseFloat(depositAmt), currency) : 'Toca para ingresar monto'}
                  </span>
                </div>
                {showKeypad && (
                  <NumericKeypad
                    value={depositAmt}
                    onChange={setDepositAmt}
                    onConfirm={() => setShowKeypad(false)}
                    currency={currency}
                    label="¿Cuánto vas a agregar?"
                  />
                )}
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn btn-secondary" style={{ flex:1 }} onClick={() => setDepositModal(null)}>Cancelar</button>
                <button className="btn btn-primary" style={{ flex:2 }} onClick={handleDeposit}>Depositar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { display:'flex', flexDirection:'column', gap:20 },
  grid: { display:'grid', gap:16, gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))' },
  empty: { display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:'60px 20px', color:'var(--text-secondary)', textAlign:'center' },
  doneBanner: {
    position:'absolute', top:0, right:0, background:'var(--green)', color:'#fff',
    padding:'4px 12px', fontSize:'0.75rem', fontWeight:700, borderRadius:'0 16px 0 12px',
  },
  amountDisplay: {
  background: 'var(--bg-input)',
  border: '1.5px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  padding: '16px',
  cursor: 'pointer',
  minHeight: 58,
  display: 'flex',
  alignItems: 'center',
  transition: 'border-color 0.2s',
},
};
